import { ReactElement, useState, MutableRefObject, useMemo } from 'react';
import { Button, Form, Heading } from '@appsumo/dorado-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FieldValues } from 'react-hook-form';

import {
  COUNTRIES_LIST,
  COUNTRIES_REQUIRE_POSTAL_CODE,
} from '~/constants/checkout';
import {
  BillingDetailsProps,
  BillingDetailsFormProps,
  FormRefProps,
} from '~/types/checkout';
import { billingSchema } from '~/schemas/billing';

import { Loader } from '~/components/ui';

export default function BillingForm({
  billingDetails,
  onClickClose,
  onClickSubmit,
  formRef,
  isLoading,
  showBusinessName,
}: Readonly<{
  billingDetails: BillingDetailsProps | null;
  onClickClose: () => void;
  onClickSubmit: (data: BillingDetailsFormProps) => Promise<void>;
  formRef: MutableRefObject<FormRefProps | undefined>;
  isLoading: boolean;
  showBusinessName?: boolean;
}>): ReactElement {
  // The default country is US, so show postal code
  const initShowPostalCode = useMemo(
    () =>
      COUNTRIES_REQUIRE_POSTAL_CODE.includes(
        `${billingDetails?.country.value ?? 'US'}`,
      ),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const [showPostalCode, setShowPostalCode] = useState(initShowPostalCode);

  function onChangeBilling(data: FieldValues, name: string, type: string) {
    if (type === 'change' && name === 'country') {
      const countryHasPostalCode = COUNTRIES_REQUIRE_POSTAL_CODE.includes(
        `${data.country.value}`,
      );

      setShowPostalCode(countryHasPostalCode);
    }
  }

  return (
    <Form
      onSubmit={onClickSubmit}
      resolver={yupResolver(billingSchema)}
      watch={onChangeBilling}
      ref={formRef}
      autoClearErrors
    >
      <div className="mt-4 flex flex-col gap-2">
        <div data-testid="billing-checkout-form-container">
          {/* Optional company name */}
          {showBusinessName && (
            <div className="mb-4" data-testid="billing-form-billing-name">
              <label className="mb-2 flex" htmlFor="billling_name">
                <Heading.H5>Business name</Heading.H5>
                <span className="ml-4 self-center text-sm text-grace">
                  optional
                </span>
              </label>
              <Form.Input
                name="billing_name"
                type="text"
                helperText="Business name will appear on your invoices, unless you add a different taxable entity below or during checkout."
                defaultValue={billingDetails?.billingName}
              />
            </div>
          )}
          <Form.Dropdown
            name="country"
            label="Country or region"
            options={COUNTRIES_LIST}
            defaultValue={billingDetails?.country ?? COUNTRIES_LIST[0]}
            heightLimit={200}
          />
          {showPostalCode && (
            <div className="mt-4">
              <Form.Input
                name="postal_code"
                label="Postal code"
                type="text"
                defaultValue={billingDetails?.postalCode}
              />
            </div>
          )}
          <div className="hidden">
            <Form.Checkbox
              name="save_billing_info"
              // @ts-ignore
              defaultChecked
            />
          </div>
        </div>
      </div>
      <div className="flex">
        <Button
          disabled={isLoading}
          tertiary
          type="button"
          className="mt-4 mr-2 flex w-full justify-center md:w-fit"
          data-testid="billing-checkout-form-cancel"
          onClick={onClickClose}
        >
          Cancel
        </Button>
        <Button
          disabled={isLoading}
          type="submit"
          className="mt-4 flex w-full justify-center md:w-fit"
          data-testid="billing-checkout-form-submit"
        >
          <span>Save</span>
          {isLoading && (
            <span className="ml-2">
              <Loader size="sm" />
            </span>
          )}
        </Button>
      </div>
    </Form>
  );
}
